// Copyright 2022, Imprivata, Inc.  All rights reserved.

import {
  type ApiErrorPayloadCommon,
  isApiError,
} from "@imprivata-cloud/common";
import type { CodingContext } from "@imprivata-cloud/data-privacy-js";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { usePlatform } from "../../context/platform/hooks";
import type { HandoffTokenStatus } from "../../models/handoff-token-status";
import { apiClient } from "../client";

export interface InitiateDeviceHandoffResponse {
  handoffUri: string;
  token: string;
  tokenStatus: HandoffTokenStatus;
}

const createInitiateDeviceHandoffRequest = async (
  codingContext: CodingContext | undefined
): Promise<InitiateDeviceHandoffResponse> => {
  const response = await apiClient.get<InitiateDeviceHandoffResponse>(
    "handoff/initiate",
    codingContext
  );
  if (!response || !response.handoffUri || !response.token) {
    throw new Error("Invalid response from handoff endpoint");
  }
  return response;
};

export const useCreateDeviceHandoffMutation = (options: {
  onSuccess: (handoffUri: InitiateDeviceHandoffResponse) => void;
}) => {
  const { codingContext } = usePlatform();
  const [apiError, setApiError] = useState<ApiErrorPayloadCommon | undefined>(
    undefined
  );
  const mutation = useMutation({
    mutationFn: () => createInitiateDeviceHandoffRequest(codingContext),
    onSuccess: (response) => {
      options.onSuccess(response);
    },
    onError(error) {
      if (isApiError(error)) {
        setApiError(error);
      } else {
        setApiError({ error: { code: "unknown", message: "Unknown error" } });
      }
    },
  });

  return {
    initiateDeviceHandoff: mutation.mutate,
    apiError,
    ...mutation,
  };
};
